// Copyright (c) StrangeBee 2023

import React, { useCallback, useEffect, useMemo, useState, ReactElement } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { urlValidator } from '@Helpers/string';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import lodash from 'lodash';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import WsConfigForm from '@Organisms/FormConfig/WsConfigForm/WsConfigForm';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { OrgSelector } from '@Organisms/FormConfig/OrgSelector/OrgSelector';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';
import useValidateForm from '@Hooks/useValidateForm';
import client from '@Client';
import useListQuery from '@Hooks/useListQuery';
import { OrganisationOutput } from 'thehive-sdk';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { Form } from 'antd';
import { handleTestServerError, openMessage } from '@Helpers/';
import { computeWsConfig } from '@Helpers/wsConfig';

import './MispDrawer.scss';

const nameKey = {
    name: ['name'],
    url: ['url'],
    auth: {
        key: ['auth', 'key'],
    },
    wsConfig: ['wsConfig'],
    purpose: ['purpose'],

    includedTheHiveOrganisations: ['includedTheHiveOrganisations'],
    excludedTheHiveOrganisations: ['excludedTheHiveOrganisations'],
    tags: ['tags'],
    maxAge: ['maxAge'],
    maxAttributes: ['max-attributes'],
    whitelist: {
        organisations: ['whitelist', 'organisations'],
        tags: ['whitelist', 'tags'],
    },
    exclusion: {
        organisations: ['exclusion', 'organisations'],
        tags: ['exclusion', 'tags'],
    },
    exportCaseTags: ['exportCaseTags'],
    exportObservableTags: ['exportObservableTags'],
};
export const resetFields = {
    name: '',
    url: '',
    auth: {
        type: 'key',
        key: '',
    },
    purpose: '',

    wsConfig: {
        proxy: {
            protocol: 'http',
        },
        ssl: {
            loose: {
                checkCertificateAuthority: true,
                acceptAnyCertificate: false,
            },
        },
    },

    filterOrganization: 'all',
    includedTheHiveOrganisations: [] as string[],
    excludedTheHiveOrganisations: [] as string[],

    tags: [],
    maxAge: null,
    ['max-attributes']: null,
    whitelist: {
        organisations: [],
        tags: [],
    },
    exclusion: {
        organisations: [],
        tags: [],
    },
    exportCaseTags: false,
    exportObservableTags: false,
};

interface MispDrawerProps {
    data?: Record<string, any>;
    mode?: 'edit' | 'add';
    triggerElem?: ReactElement;
    visible?: boolean;
    afterVisible?: (visible: boolean) => void;
    onSubmit: (state: any) => void;
    className?: string;
}

const MispDrawer = ({
    data,
    className,
    onSubmit,
    triggerElem,
    mode = 'edit',
    visible,
    afterVisible,
}: MispDrawerProps): ReactElement => {
    const i = useLocalMessage();

    const dataCurrent = data;

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const [initialValues, setInitialValues] = useState<Record<string, any>>({});
    const [filterUsed, setFilterUsed] = useState<string>();
    const purpose = Form.useWatch(nameKey.purpose, form);
    const [testLoading, setTestLoading] = useState<boolean>(false);

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listOrganisation' }],
                name: 'get-all-organisations',
            },
            sort: ['+name'],
        },
        controller: client.OrganisationController,
    });

    const acceptedNameOrg = useMemo(() => orgList.map(({ name }) => name), [orgList]);

    const computeConfig = useCallback(
        (formValues: any) => {
            const result = lodash.merge(dataCurrent, formValues);
            delete result.another;

            const tmpFilter = result.filterOrganization;

            const filterOrganization = {
                includedTheHiveOrganisations: ['*'],
                excludedTheHiveOrganisations: [] as string[],
            };

            if (tmpFilter === 'include') {
                filterOrganization.includedTheHiveOrganisations = formValues.includedTheHiveOrganisations.filter(
                    (name: string) => name !== '*' && acceptedNameOrg.includes(name),
                );
                filterOrganization.excludedTheHiveOrganisations = [];
            }

            if (tmpFilter === 'exclude') {
                filterOrganization.includedTheHiveOrganisations = ['*'];
                filterOrganization.excludedTheHiveOrganisations = formValues.excludedTheHiveOrganisations.filter(
                    (name: string) => acceptedNameOrg.includes(name),
                );
            }
            const wsConfig = computeWsConfig(result.wsConfig, formValues.wsConfig);

            const newResult = {
                ...result,
                auth: { ...formValues.auth, type: 'key' },
                wsConfig,
                ...filterOrganization,
                tags: formValues.tags,
                whitelist: formValues.whitelist,
                exclusion: formValues.exclusion,
            };

            return newResult;
        },
        [acceptedNameOrg, dataCurrent],
    );

    const handleTest = () => {
        const formValues = form.getFieldsValue();
        const config = computeConfig(formValues);

        setTestLoading(true);
        client.MispController.test(config)
            .then(() => {
                openMessage('success', i('configPlateform.message.misp.testSuccess'));
            })
            .catch(handleTestServerError)
            .finally(() => setTestLoading(false));
    };
    const computeOnFinishHandler = (toggleDrawer: () => void) => (formValues: any) => {
        const config = computeConfig(formValues);

        onResetForm();
        toggleDrawer();
        setInitialValues(config);
        onSubmit(config);
    };

    const onChangeFilter = (value: string) => {
        setFilterUsed(value);
    };

    const onResetForm = useCallback(() => {
        setFieldsTouched(false);
        onChangeFilter(initialValues.filterOrganization);
        form.resetFields();
    }, [form, initialValues.filterOrganization, setFieldsTouched]);

    useEffect(() => {
        let filterOrganization = initialValues.filterOrganization || 'all';

        if (initialValues.excludedTheHiveOrganisations && initialValues.excludedTheHiveOrganisations.length) {
            filterOrganization = 'exclude';
        }

        if (
            initialValues.includedTheHiveOrganisations &&
            initialValues.includedTheHiveOrganisations.length &&
            !initialValues.includedTheHiveOrganisations.includes('*')
        ) {
            filterOrganization = 'include';
        }
        onChangeFilter(filterOrganization);
        form.setFieldsValue({ ...initialValues, filterOrganization });
    }, [initialValues]); //eslint-disable-line

    useEffect(() => {
        const dataCurrentClone = lodash.cloneDeep(dataCurrent);
        const resetFiedsClone = lodash.cloneDeep(resetFields);
        const result = lodash.merge(resetFiedsClone, dataCurrentClone);
        result.wsConfig.ssl.loose.checkCertificateAuthority = !result.wsConfig.ssl.loose.acceptAnyCertificate;

        setInitialValues(result);
    }, [dataCurrent]); //eslint-disable-line

    return (
        <THDrawer
            title={
                mode === 'edit'
                    ? i('configPlateform.drawer.titleEdit', { name: dataCurrent?.name || '' })
                    : i('configPlateform.drawer.titleAdd')
            }
            className={classList('config-misp__drawer', className)}
            triggerElem={triggerElem}
            visible={visible}
            disableClose={fieldsTouched}
            afterVisibleChange={(visible) => {
                afterVisible?.(visible);
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    autoComplete="off"
                    onFinish={computeOnFinishHandler(toggleDrawer)}
                    onValuesChange={() => {
                        setFieldsTouched(true);
                        onFormValuesChange();
                    }}
                    form={form}
                    initialValues={initialValues}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid}
                                onCancel={() => {
                                    onResetForm();
                                    toggleDrawer();
                                }}
                                footerText={i(
                                    mode === 'edit'
                                        ? 'configPlateform.action.confirmeEdit'
                                        : 'configPlateform.action.confirmeAdd',
                                )}
                                anotherText={i('configPlateform.action.testConnection')}
                                onAnother={handleTest}
                                anotherDisabled={!isValid && mode !== 'edit'}
                                anotherLoading={testLoading}
                            />
                        }
                    >
                        <div className="misp-form__section">
                            <TitleSection>{i('configPlateform.section.principal')}</TitleSection>
                            <THForm.Item
                                name={nameKey.name}
                                label={i('configPlateform.field.nameServer')}
                                rules={[{ required: true, message: i('configPlateform.required.general') }]}
                            >
                                <THInput />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.url}
                                label={i('configPlateform.field.urlServer')}
                                rules={[
                                    { required: true, message: i('configPlateform.required.general') },
                                    {
                                        validator: urlValidator,
                                        message: i('configPlateform.required.url'),
                                    },
                                ]}
                            >
                                <THInput />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.auth.key}
                                label={i('configPlateform.field.api-key')}
                                rules={[{ required: true, message: i('configPlateform.required.general') }]}
                            >
                                <THPassword />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.purpose}
                                label={i('configPlateform.field.purpose')}
                                rules={[{ required: true, message: i('configPlateform.required.general') }]}
                            >
                                <THSelect
                                    options={[
                                        { label: i('configPlateform.field.importOnly'), value: 'ImportOnly' },
                                        { label: i('configPlateform.field.exportOnly'), value: 'ExportOnly' },
                                        {
                                            label: i('configPlateform.field.ImportAndExport'),
                                            value: 'ImportAndExport',
                                        },
                                    ]}
                                />
                            </THForm.Item>
                        </div>
                        <div className="misp-form__section">
                            <WsConfigForm name={['wsConfig']} form={form} />
                        </div>
                        <div className="misp-form__section">
                            <TitleSection>{i('configPlateform.section.advancedSetting')}</TitleSection>
                            <THForm.Item
                                name={'filterOrganization'}
                                label={i('configPlateform.field.filterOrganization')}
                            >
                                <THSelect
                                    placeholder="Choose filter organisation"
                                    options={[
                                        { value: 'all', label: i('configPlateform.options.all') },
                                        { value: 'include', label: i('configPlateform.options.include') },
                                        { value: 'exclude', label: i('configPlateform.options.exlude') },
                                    ]}
                                    onChange={onChangeFilter}
                                />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.includedTheHiveOrganisations}
                                label={i('configPlateform.field.includedTheHiveOrganisations')}
                                hidden={filterUsed !== 'include'}
                            >
                                <OrgSelector />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.excludedTheHiveOrganisations}
                                label={i('configPlateform.field.excludedTheHiveOrganisations')}
                                hidden={filterUsed !== 'exclude'}
                            >
                                <OrgSelector />
                            </THForm.Item>
                            <THForm.Item name={nameKey.tags} label={i('configPlateform.field.tags')}>
                                <THTagSelector />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.exportCaseTags}
                                label={i('configPlateform.field.exportCaseTags')}
                                hidden={purpose === 'ImportOnly'}
                                valuePropName="checked"
                            >
                                <THSwitch />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.exportObservableTags}
                                label={i('configPlateform.field.exportObservableTags')}
                                hidden={purpose === 'ImportOnly'}
                                valuePropName="checked"
                            >
                                <THSwitch />
                            </THForm.Item>
                        </div>
                        <div className="misp-form__section">
                            <TitleSection>{i('configPlateform.section.filter')}</TitleSection>
                            <THForm.Item name={nameKey.maxAge} label={i('configPlateform.field.max-age')}>
                                <THNumberSelect options={['minutes', 'hours', 'days']} />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.whitelist.organisations}
                                label={i('configPlateform.field.whitelistOrganisations')}
                            >
                                <THSelect mode="tags" />
                            </THForm.Item>
                            <THForm.Item
                                name={nameKey.exclusion.organisations}
                                label={i('configPlateform.field.exclusionOrganisations')}
                            >
                                <THSelect mode="tags" />
                            </THForm.Item>
                            <THForm.Item name={nameKey.maxAttributes} label={i('configPlateform.field.max-attributes')}>
                                <THInputNumber />
                            </THForm.Item>
                            <THForm.Item name={nameKey.whitelist.tags} label={i('configPlateform.field.whitelisTags')}>
                                <THTagSelector />
                            </THForm.Item>
                            <THForm.Item name={nameKey.exclusion.tags} label={i('configPlateform.field.exclusionTags')}>
                                <THTagSelector />
                            </THForm.Item>
                        </div>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};

export default MispDrawer;
